import L from 'leaflet';
import { useCallback, type ReactNode } from 'react';
import { Form, Stack } from 'react-bootstrap';
import CustomLeafletControl from './CustomLeafletControl';

export type MapViewContentProps = {
  satelliteView: boolean;
  labelsEnabled: boolean;
  onToggleSatellite: () => void;
  onToggleLabels: () => void;
  extra?: ReactNode;
  hideLabelsToggle?: boolean;
} & {};

export type MapViewProps = {
  satelliteView: boolean;
  labelsEnabled: boolean;
  onToggleSatellite: () => void;
  onToggleLabels: () => void;
  extra?: ReactNode;
  hideLabelsToggle?: boolean;
  actionControl?: ReactNode | ((props: MapViewContentProps) => ReactNode) | null;
  expandedContent?: ReactNode | ((props: MapViewContentProps) => ReactNode) | null;
} & {};

const DefaultMapViewContent = ({
  satelliteView,
  labelsEnabled,
  onToggleSatellite,
  onToggleLabels,
  extra,
  hideLabelsToggle = false,
}: MapViewContentProps) => {
  return (
    <Stack className="leaflet-bar map-view-control bg-white p-2 pb-0 rounded-3">
      {extra}
      {satelliteView && !hideLabelsToggle && (
        <Form.Check
          id="map-label-toggle"
          type="checkbox"
          className="map-view-toggle"
          label={<span>Labels</span>}
          checked={labelsEnabled}
          onChange={onToggleLabels}
        />
      )}
      <Form.Check
        type="switch"
        id="map-satellite-toggle"
        label={<span>{satelliteView ? 'Satellite' : 'Map'}</span>}
        className="map-view-toggle map-view-satellite-toggle"
        checked={satelliteView}
        onChange={onToggleSatellite}
      />
    </Stack>
  );
};

const MapView = ({
  satelliteView,
  labelsEnabled,
  onToggleSatellite,
  onToggleLabels,
  extra,
  actionControl = null,
  expandedContent = null,
  hideLabelsToggle = false,
}: MapViewProps) => {
  const viewProps: MapViewContentProps = {
    satelliteView,
    labelsEnabled,
    onToggleSatellite,
    onToggleLabels,
    extra,
    hideLabelsToggle,
  };
  const resolvedActionControl =
    typeof actionControl === 'function' ? actionControl(viewProps) : actionControl;
  const resolvedExpandedContent =
    typeof expandedContent === 'function' ? expandedContent(viewProps) : expandedContent;

  // Callback ref so click propagation is disabled exactly when the node is
  // attached. The control mounts via a portal that isn't ready on first
  // commit, so an effect keyed on a stable ref would run too early and never
  // re-run once the element finally exists.
  //
  // Leaflet stops mousedown/touchstart at the element it is given. React handles
  // the portal's events at the portal container, which is this node's parent, so
  // the stop goes on the parent: React still receives mousedown (react-select opens
  // its menu on it), and the map still never does.
  const setMapControlsRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      L.DomEvent.disableClickPropagation(node.parentElement ?? node);
    }
  }, []);

  return (
    <CustomLeafletControl position="bottomleft">
      <div className="d-inline-flex flex-column align-items-start gap-1" ref={setMapControlsRef}>
        <div className="d-inline-flex align-items-stretch gap-2">
          <DefaultMapViewContent {...viewProps} />
          {resolvedActionControl && (
            <div className="leaflet-bar rounded-3 overflow-hidden d-inline-flex align-items-stretch justify-content-stretch">
              {resolvedActionControl}
            </div>
          )}
        </div>
        {resolvedExpandedContent}
      </div>
    </CustomLeafletControl>
  );
};

export default MapView;
